import type { Maybe } from '@evc/web-components';

import { FILE_ICON, FILE_ICON_CONTENT_TYPE_MAP, type FileIcon } from './file.enum';

/** Fallback by MIME top-level type when the subtype is unknown (e.g. `image/bmp`). */
const MIME_FAMILY_ICON: Record<string, FileIcon> = {
  image: FILE_ICON.IMAGE,
  video: FILE_ICON.VIDEO,
  text: FILE_ICON.TEXT,
};

const TYPE_TO_ICON = new Map<string, FileIcon>(
  Object.entries(FILE_ICON_CONTENT_TYPE_MAP).flatMap(([icon, types]) =>
    types.map((type) => [type, icon as FileIcon] as const),
  ),
);

/**
 * Resolve the file icon from a MIME type (parameters such as `; charset=utf-8` are ignored).
 * Falls back to the MIME family (`image/*`, `video/*`, `text/*`), then to the generic `file` icon.
 *
 * @example ```ts
 * getFileIconFromMimeType('application/pdf') // 'pdf'
 * getFileIconFromMimeType('image/bmp')       // 'image'
 * getFileIconFromMimeType('application/octet-stream') // 'file'
 * ```
 */
export function getFileIconFromMimeType(mimeType: Maybe<string>): FileIcon {
  const [family, subtype] = (mimeType?.split(';')[0].trim().toLowerCase() ?? '').split('/');
  if (!subtype) return FILE_ICON.FILE;

  return TYPE_TO_ICON.get(subtype) ?? MIME_FAMILY_ICON[family] ?? FILE_ICON.FILE;
}

/**
 * Resolve the file icon from a file name or path, using its extension.
 * Falls back to the generic `file` icon.
 *
 * @example ```ts
 * getFileIconFromPath('report.PDF')          // 'pdf'
 * getFileIconFromPath('scans/weld-01.png')   // 'image'
 * getFileIconFromPath('README')              // 'file'
 *
 * <evc-icon [file]="getFileIconFromPath(file.name)" />
 * ```
 */
export function getFileIconFromPath(path: Maybe<string>): FileIcon {
  const fileName = path?.trim().split(/[\\/]/).pop()?.toLowerCase() ?? '';
  const dotIndex = fileName.lastIndexOf('.');
  // A dot at index 0 is a dotfile's leading dot, not an extension separator.
  if (dotIndex <= 0) return FILE_ICON.FILE;

  return TYPE_TO_ICON.get(fileName.slice(dotIndex + 1)) ?? FILE_ICON.FILE;
}
